import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';
import EditTestModal from './EditTestModal';

const { FiExternalLink, FiCopy, FiCheckCircle, FiActivity, FiClock, FiSearch, FiRefreshCcw, FiEdit3 } = FiIcons;

const TestHistory = ({ userId, refreshKey, profile }) => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingTest, setEditingTest] = useState(null);

  const fetchTests = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('app_tests')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTests(data || []);
    } catch (err) {
      console.error("Error fetching tests:", err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchTests();
  }, [fetchTests, refreshKey]);

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    alert('Link copiado com sucesso!');
  };

  const filteredTests = tests.filter(test => 
    test.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    test.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-2">
        <div className="relative flex-1">
          <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input 
            type="text"
            placeholder="Buscar testes por nome ou categoria..."
            className="w-full pl-12 pr-4 py-4 bg-white border border-slate-100 rounded-3xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-bold text-slate-900 text-sm shadow-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button 
          onClick={fetchTests}
          className="p-4 bg-white border border-slate-100 rounded-2xl text-slate-400 hover:text-indigo-600 hover:bg-slate-50 transition-all shadow-sm"
        >
          <SafeIcon icon={FiRefreshCcw} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="overflow-hidden rounded-[32px] border border-slate-100 bg-white shadow-sm overflow-x-auto">
        {loading && tests.length === 0 ? (
          <div className="p-20 text-center">
            <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-400 font-black uppercase italic text-xs">Carregando histórico...</p>
          </div>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50/50 border-b border-slate-100">
                <th className="py-5 px-8 text-[10px] font-black uppercase tracking-wider text-slate-400 italic">Identificação</th>
                <th className="py-5 px-6 text-[10px] font-black uppercase tracking-wider text-slate-400 italic">Categoria</th>
                <th className="py-5 px-6 text-[10px] font-black uppercase tracking-wider text-slate-400 italic text-center">Data</th>
                <th className="py-5 px-6 text-[10px] font-black uppercase tracking-wider text-slate-400 italic">Status</th>
                <th className="py-5 px-8 text-[10px] font-black uppercase tracking-wider text-slate-400 italic text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filteredTests.length === 0 ? (
                <tr>
                  <td colSpan="5" className="py-24 text-center">
                    <div className="bg-slate-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                      <SafeIcon icon={FiClock} className="text-3xl text-slate-300" />
                    </div>
                    <h4 className="text-slate-900 font-black uppercase italic text-lg">Nenhum teste encontrado</h4>
                    <p className="text-slate-400 text-[10px] font-black uppercase mt-2">Solicite seu primeiro teste para começar a avaliar</p>
                  </td>
                </tr>
              ) : (
                filteredTests.map((test) => (
                  <motion.tr layout key={test.id} className="hover:bg-slate-50/50 transition-colors group">
                    <td className="py-5 px-8">
                      <div className="flex flex-col">
                        <span className="font-bold text-slate-900 leading-tight">{test.title}</span>
                        <span className="text-[9px] text-slate-400 font-black uppercase tracking-tighter mt-0.5">ID: {test.id.slice(0, 8)}</span>
                      </div>
                    </td>
                    <td className="py-5 px-6">
                      <span className="px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-[10px] text-[9px] font-black uppercase italic">
                        {test.category || 'Geral'}
                      </span>
                    </td>
                    <td className="py-5 px-6 text-[11px] font-bold text-slate-500 text-center">
                      {new Date(test.created_at).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="py-5 px-6">
                      {test.status === 'completed' ? (
                        <span className="flex items-center gap-1.5 text-emerald-500 text-[10px] font-black uppercase italic">
                          <SafeIcon icon={FiCheckCircle} /> Concluído
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5 text-indigo-600 text-[10px] font-black uppercase italic">
                          <SafeIcon icon={FiActivity} /> Ativo
                        </span>
                      )}
                    </td>
                    <td className="py-5 px-8 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button 
                          onClick={() => setEditingTest(test)}
                          className="p-2.5 bg-slate-50 text-slate-400 hover:text-indigo-600 hover:bg-slate-100 rounded-xl transition-all"
                          title="Editar"
                        >
                          <SafeIcon icon={FiEdit3} />
                        </button>
                        <button 
                          onClick={() => copyToClipboard(test.test_url)}
                          className="p-2.5 bg-slate-50 text-slate-400 hover:text-indigo-600 hover:bg-slate-100 rounded-xl transition-all"
                          title="Copiar Link"
                        >
                          <SafeIcon icon={FiCopy} />
                        </button>
                        <a 
                          href={test.test_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2.5 bg-slate-50 text-slate-400 hover:text-indigo-600 hover:bg-slate-100 rounded-xl transition-all"
                        >
                          <SafeIcon icon={FiExternalLink} />
                        </a>
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      <EditTestModal 
        isOpen={!!editingTest}
        onClose={() => setEditingTest(null)}
        test={editingTest}
        profile={profile}
        onSuccess={fetchTests}
      />
    </div>
  );
};

export default TestHistory;